<template>
  <div
    class="material-input"
    :class="{ white: bgColor, disabled }"
    :id="`minput_${unique_id}`"
  >
    <input
      class="autofill-preventer"
      type="password"
      :name="decoy_name"
      autocomplete="false"
      v-if="preventAutofill"
    />
    <!-- this is here to prevent autofill in Chrome -->
    <input
      :type="input_type"
      :id="`${name}_${unique_id}`"
      v-bind:value="value"
      v-on:input="$emit('input', $event.target.value)"
      :class="{
        hasValue: value || value === 0 || value === '0' || type === 'number',
      }"
      :required="required"
      :name="`${name}_${unique_id}`"
      :autocomplete="!!autocomplete ? autocomplete : (enableAutocomplete ? `${name}_${unique_id}` : 'off')"
      v-if="input_type !== 'textarea'"
      :maxlength="maxChars"
      ref="field"
      :readonly="readonly"
      @focus="onFocus"
      :disabled="disabled"
    />

    <textarea
      :name="`${name}_${unique_id}`"
      v-bind:value="value"
      v-on:input="$emit('input', $event.target.value)"
      :class="{ hasValue: value || value === 0 || value === '0' }"
      :required="required"
      :id="`${name}_${unique_id}`"
      :autocomplete="!!autocomplete ? autocomplete : (enableAutocomplete ? `${name}_${unique_id}` : 'off')"
      v-if="input_type === 'textarea'"
      :maxlength="maxChars"
      v-autosize="value"
      rows="1"
      ref="field"
      :readonly="readonly"
      :disabled="disabled"
      @blur="$emit('blur')"
    ></textarea>

    <span>{{ label }}</span>

    <div v-if="input_type === 'number'" class="currency">{{ currency }}</div>

    <i
      class="material-icons"
      v-if="type === 'password'"
      @click="togglePassword()"
      >visibility{{ input_type === 'password' ? '' : '_off' }}</i
    >

    <p class="charCount" v-if="maxChars !== 100000">
      {{ value.length }}/{{ maxChars }}
    </p>
  </div>
</template>

<script>
import numeral from 'numeral';

export default {
  name: 'MaterialInput',
  props: {
    label: String,
    value: [String, Number],
    name: String,
    type: String,
    symbol: String,
    required: {
      type: Boolean,
      default: false,
    },
    bgColor: String,
    preventAutofill: {
      type: Boolean,
      default: false,
    },
    maxChars: {
      type: Number,
      default: 100000,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    enableAutocomplete: {
      type: Boolean,
      default: true
    },
    autocomplete: {
      type: String
    }
  },
  data() {
    return {
      input_type: 'text',
      unique_id: Math.floor(Date.now() * Math.random()),
      readonly: false,
    };
  },
  methods: {
    togglePassword() {
      if (this.input_type === 'password') {
        this.input_type = 'text';
      } else {
        this.input_type = 'password';
      }
    },
    onFocus() {
      if (this.type === 'number' && (this.value === 0 || this.value === '0')) {
        this.$emit('input', '');
      }
    },
  },
  computed: {
    decoy_name() {
      return 'blah-' + this.unique_id;
    },
    currency() {
      if (this.input_type === 'number') {
        return this.symbol + numeral(this.value).format('0,0.[00]');
      }
      return '';
    },
  },
  mounted() {
    this.input_type = this.type;

    if (this.preventAutofill) {
      this.readonly = true;
      setTimeout(() => {
        this.readonly = false;
      }, 2000);
    }
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/_styles.scss';

.autofill-preventer {
  opacity: 0;
  position: fixed;
  top: -3000px;
  left: -3000px;
  display: none;
}

.material-input {
  position: relative;
  padding-top: 15px;

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  textarea:-webkit-autofill,
  textarea:-webkit-autofill:hover,
  textarea:-webkit-autofill:focus {
    -webkit-box-shadow: inset 0 0 0px 9999px $bg-color;
    box-shadow: inset 0 0 0px 9999px $bg-color;
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.7;
  }

  &.white {
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    textarea:-webkit-autofill,
    textarea:-webkit-autofill:hover,
    textarea:-webkit-autofill:focus {
      -webkit-box-shadow: inset 0 0 0px 9999px white;
      box-shadow: inset 0 0 0px 9999px white;
    }
  }

  input,
  textarea {
    outline: none !important;
    border: none;
    border-bottom: 1px solid #dce0e9;
    height: 32px;
    padding: 0;
    margin-bottom: 25px !important;
    display: block;
    transition: border 0.3s;
    background: transparent;
    width: 100%;
    resize: vertical;
    color: #222;

    &:focus,
    &:active,
    &.hasValue,
    &:-webkit-autofill {
      + span {
        text-transform: uppercase;
        font-size: 10px;
        font-weight: 500;
        top: 1px;
      }
    }

    &:hover {
      resize: vertical;
    }

    &:focus,
    &:active {
      border-bottom-color: #b6bac4;
      resize: vertical;

      ~ .charCount {
        opacity: 1;
      }
    }

    &[type='number'] {
      color: transparent;

      &:focus,
      &:active {
        color: #222;
      }
    }

    + span {
      position: absolute;
      top: 23px;
      font-size: 14px;
      font-weight: 400;
      transition: all 0.3s;
      pointer-events: none;
      color: #929da9;
      margin: 0;
      width: 100%;
    }

    ~ i {
      position: absolute;
      right: -5px;
      top: 17px;
      user-select: none;
      cursor: pointer;
      padding: 5px;
      font-size: 18px;
      z-index: 5;
    }
  }
}

.charCount {
  position: absolute;
  right: 0;
  font-size: 11px;
  bottom: -34px;
  color: #acb6c1;
  opacity: 0;
  transition: opacity 0.2s;
}

.currency {
  position: absolute;
  user-select: none;
  pointer-events: none;
  top: 19px;
  left: 0px;
}

input:active,
input:focus {
  ~ .currency {
    left: -99999px;
    visibility: hidden;
  }
}
</style>
